import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';

/**
 * Footer component – renders the site‑wide footer.
 * The component now guards against the unlikely case where `useLocation`
 * returns `undefined` (e.g. when the component is rendered outside a `<Router>`),
 * and it uses a more explicit fallback for the home‑page link.
 */
export default function Footer() {
  const location = useLocation();

  // Defensive programming – if `location` is somehow undefined,
  // fall back to a safe default pathname.
  const pathname = useMemo(() => location?.pathname ?? '/', [location]);

  // Determine if we are on a Dutch sub‑site.
  const isNL = pathname.startsWith('/nl');
  // Prefix is either '/nl' or an empty string.
  const prefix = isNL ? '/nl' : '';

  // Helper to safely build URLs – ensures we never produce a double slash.
  const buildUrl = (path: string) => `${prefix}${path}`.replace(/^\/\/+/, '/');

  return (
    <footer className="bg-gray-900 text-white pt-16 pb-8">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          <div className="lg:col-span-2">
            {/* Home link – explicit fallback to '/' if `prefix` is empty */}
            <a href={prefix ? prefix : '/'} className="block mb-4 hover:opacity-90 transition-opacity -ml-5">
              <img
                src="https://static.readdy.ai/image/2f7102ba1bcef2b8369bef774bca35e3/da6cb77533abe0b369e4f2ba82f939f2.png"
                alt="Thyamis Logo"
                className="h-16 w-auto"
              />
            </a>
            <p className="text-gray-400 mb-6 leading-relaxed">
              {isNL
                ? 'Uw betrouwbare partner voor uitgebreide IT-oplossingen, hardware, software en geavanceerde AI-diensten.'
                : 'Your trusted partner for comprehensive IT solutions, hardware, software, and cutting\u2011edge AI services.'}
            </p>
            <div className="flex space-x-4">
              <a
                href="#"
                className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-primary transition-colors cursor-pointer"
              >
                <i className="ri-facebook-fill"></i>
              </a>
              <a
                href="#"
                className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-primary transition-colors cursor-pointer"
              >
                <i className="ri-twitter-x-fill"></i>
              </a>
              <a
                href="https://www.linkedin.com/company/thyamis/about/"
                className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-primary transition-colors cursor-pointer"
              >
                <i className="ri-linkedin-fill"></i>
              </a>
              <a
                href="#"
                className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-primary transition-colors cursor-pointer"
              >
                <i className="ri-youtube-fill"></i>
              </a>
            </div>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{isNL ? 'Producten' : 'Products'}</h3>
            <ul className="space-y-3">
              <li>
                <a href={buildUrl('/products/laptops-desktops')} className="text-gray-400 hover:text-white transition-colors">
                  Hardware
                </a>
              </li>
              <li>
                <a href={buildUrl('/microsoft-365')} className="text-gray-400 hover:text-white transition-colors">
                  Software
                </a>
              </li>
              <li>
                <a href={buildUrl('/products/routers-switches')} className="text-gray-400 hover:text-white transition-colors">
                  {isNL ? 'Netwerken' : 'Networking'}
                </a>
              </li>
              <li>
                <a href={buildUrl('/antivirus-security')} className="text-gray-400 hover:text-white transition-colors">
                  {isNL ? 'Beveiliging' : 'Security'}
                </a>
              </li>
              <li>
                <a href={buildUrl('/ai-consulting')} className="text-gray-400 hover:text-white transition-colors">
                  {isNL ? 'AI-oplossingen' : 'AI Solutions'}
                </a>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{isNL ? 'Diensten' : 'Services'}</h3>
            <ul className="space-y-3">
              <li>
                <a href={buildUrl('/it-consulting')} className="text-gray-400 hover:text-white transition-colors">
                  {isNL ? 'IT-advies' : 'IT Consulting'}
                </a>
              </li>
              <li>
                <a href={buildUrl('/cloud-services')} className="text-gray-400 hover:text-white transition-colors">
                  {isNL ? 'Clouddiensten' : 'Cloud Services'}
                </a>
              </li>
              <li>
                <a href={buildUrl('/managed-it-services')} className="text-gray-400 hover:text-white transition-colors">
                  {isNL ? 'Beheerde Diensten' : 'Managed Services'}
                </a>
              </li>
              <li>
                <a href={buildUrl('/cyber-security')} className="text-gray-400 hover:text-white transition-colors">
                  {isNL ? 'Cyberbeveiliging' : 'Cybersecurity'}
                </a>
              </li>
              <li>
                <a href={buildUrl('/ai-consulting')} className="text-gray-400 hover:text-white transition-colors">
                  {isNL ? 'AI-implementatie' : 'AI Implementation'}
                </a>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{isNL ? 'Bedrijf' : 'Company'}</h3>
            <ul className="space-y-3">
              <li>
                <a href={buildUrl('/about')} className="text-gray-400 hover:text-white transition-colors">
                  {isNL ? 'Over Ons' : 'About Us'}
                </a>
              </li>
              <li>
                <a href={buildUrl('/support')} className="text-gray-400 hover:text-white transition-colors">
                  {isNL ? 'Ondersteuningscentrum' : 'Support Center'}
                </a>
              </li>
              <li>
                <a href={buildUrl('/careers')} className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  {isNL ? 'Vacatures' : 'Careers'}
                </a>
              </li>
              <li>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  {isNL ? 'Partners' : 'Partners'}
                </a>
              </li>
              <li>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  Blog
                </a>
              </li>
              <li>
                <a href={buildUrl('/contact')} className="text-gray-400 hover:text-white transition-colors">
                  Contact
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom section */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center mb-6">
            <div className="mb-4 md:mb-0">
              <p className="text-gray-400 text-sm">
                {isNL
                  ? '\u00A9 2025 Thyamis. Alle rechten voorbehouden.'
                  : '\u00A9 2025 Thyamis. All rights reserved.'}
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-6">
              <a href={buildUrl('/privacy-policy')} className="text-gray-400 hover:text-primary text-sm transition-colors">
                {isNL ? 'Privacybeleid' : 'Privacy Policy'}
              </a>
              <a href={buildUrl('/terms-of-service')} className="text-gray-400 hover:text-primary text-sm transition-colors">
                {isNL ? 'Servicevoorwaarden' : 'Terms of Service'}
              </a>
              <a href={buildUrl('/cookie-policy')} className="text-gray-400 hover:text-primary text-sm transition-colors">
                {isNL ? 'Cookiebeleid' : 'Cookie Policy'}
              </a>
            </div>
          </div>

          <div className="flex justify-center">
            <p className="text-gray-500 text-sm">
              &copy; {new Date().getFullYear()} Thyamis. {isNL ? 'Alle rechten voorbehouden.' : 'All rights reserved.'} |{' '}
              <a
                href="https://readdy.ai/?ref=logo"
                rel="nofollow"
                className="text-gray-500 hover:text-gray-400 transition-colors"
              >
                Powered by Readdy
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
